// eslint-disable-next-line import/no-extraneous-dependencies -- '@cornerstonejs/core' should be listed in the project's dependencies. Run 'npm i -S @cornerstonejs/core' to add it
import * as cornerstone from '@cornerstonejs/core';
// eslint-disable-next-line import/no-extraneous-dependencies -- '@cornerstonejs/core' should be listed in the project's dependencies. Run 'npm i -S @cornerstonejs/core' to add it
import { deepMerge } from '@cornerstonejs/core/dist/esm/utilities';
import {
  ArrowAnnotateTool,
  CircleROITool,
  EllipticalROITool,
  LengthTool,
  MagnifyTool,
  PanTool,
  PlanarFreehandROITool,
  ProbeTool,
  RectangleROITool,
  ToolGroupManager,
  WindowLevelTool,
  ZoomToolMouseWheelTool,
  addTool,
  annotation,
  init as cornerstone3DToolsInit,
} from '@cornerstonejs/tools';

import registerWebImageLoader from '../../utils/cornerstone/registerWebImageLoader';

export const toolGroupId = 'tool-group';
export const thumbnailToolGroupId = 'thumbnail-tool-group';

export const initializeCornerstone = async () => {
  if (cornerstone.isCornerstoneInitialized()) {
    return;
  }
  await cornerstone.init({
    detectGPUConfig: {},
    rendering: {
      preferSizeOverAccuracy: true,
      useNorm16Texture: true,
      strictZSpacingForVolumeViewport: false,
      useCPURendering: false,
    },
  });
  registerWebImageLoader(cornerstone.imageLoader);
  cornerstone3DToolsInit();

  try {
    addTool(RectangleROITool);
    addTool(EllipticalROITool);
    addTool(LengthTool);
    addTool(MagnifyTool);
    addTool(ZoomToolMouseWheelTool);
    addTool(PanTool);
    addTool(ProbeTool);
    addTool(PlanarFreehandROITool);
    addTool(WindowLevelTool);
    addTool(CircleROITool);
    addTool(ArrowAnnotateTool);
  } catch {
    // Tools have already been added
  }
};

export const RENDERING_ENGINE = 'rendering-engine';

export const initializeRenderingEngine = () => {
  const renderingEngine = cornerstone.getRenderingEngine(RENDERING_ENGINE);
  if (renderingEngine) {
    return renderingEngine;
  }
  return new cornerstone.RenderingEngine(RENDERING_ENGINE);
};

export const initializeCornerstoneElement = (
  element: HTMLDivElement,
  viewportId: string,
  renderingEngine: cornerstone.Types.IRenderingEngine,
) => {
  if (
    !element ||
    !viewportId ||
    !renderingEngine ||
    cornerstone.getEnabledElement(element)
  ) {
    return;
  }
  const viewportInput = {
    viewportId,
    type: cornerstone.Enums.ViewportType.STACK,
    element,
  };
  try {
    renderingEngine.enableElement(viewportInput);
  } catch {
    /**
     This avoids a white screen when In CPU rendering in which we sometimes
    encounter a state where we attempt to disable an element that has already been cleared 
     */
  }
};

export const initializeToolsForImage = (
  viewportId: string,
  groupId: typeof toolGroupId | typeof thumbnailToolGroupId,
) => {
  if (!ToolGroupManager.getToolGroup(groupId)) {
    const toolGroup = ToolGroupManager.createToolGroup(groupId);
    toolGroup?.addTool(RectangleROITool.toolName);
    toolGroup?.addTool(ProbeTool.toolName);
    toolGroup?.addTool(PlanarFreehandROITool.toolName);
    toolGroup?.addTool(CircleROITool.toolName);
    toolGroup?.addTool(ArrowAnnotateTool.toolName);
    toolGroup?.addTool(EllipticalROITool.toolName, {
      configuration: {
        centerPointRadius: 4,
      },
    });
    toolGroup?.addTool(MagnifyTool.toolName);
    toolGroup?.addTool(LengthTool.toolName, {
      configuration: {
        handleRadius: 2,
      },
    });
    toolGroup?.addTool(ZoomToolMouseWheelTool.toolName);
    toolGroup?.addTool(PanTool.toolName);
    toolGroup?.addTool(WindowLevelTool.toolName);
  }
  const toolGroup = ToolGroupManager.getToolGroup(groupId);
  toolGroup?.addViewport(viewportId, 'rendering-engine');
  const styles = annotation.config.style.getDefaultToolStyles();
  const newStyles = {
    global: {
      textBoxVisibility: false,
      colorHighlighted: 'rgb(255, 255, 255)',
      lineWidth: 2,
    },
  };
  annotation.config.style.setDefaultToolStyles(deepMerge(styles, newStyles));
};
